import { useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt } from '../state.tsx';
import { Loading, useLoad, go, ItemIcon, ItemName, Panel } from '../components/ui.tsx';
import { StatIcon } from '../art/Scenes.tsx';
import { MarketTab, sellOnMarket } from './Market.tsx';
import { AuctionTab } from './Auction.tsx';
import { TradesTab } from './Trades.tsx';
import { RarityTag } from '../components/v08.tsx';

const CATS: [string, string][] = [['weapon', 'Weapons'], ['armor', 'Armor'], ['vehicle', 'Vehicles'], ['supply', 'Job Supplies']];
const OWNED_CATS: [string, string][] = [['consumable', 'Usable Items'], ...CATS];

export function InventoryPage({ tab = 'items' }: { tab?: string }) {
  const { me, act } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/inventory'), [me?.cash, me?.jobsDone]);
  return (
    <div>
      <div className="subnav" style={{ marginBottom: 8 }}>
        <a className={tab === 'items' ? 'active' : ''} href="#/inventory/items">My Inventory</a>
        <a className={tab === 'store' ? 'active' : ''} href="#/inventory/store">Store</a>
        <a className={tab === 'market' ? 'active' : ''} href="#/inventory/market">Marketplace</a>
        <a className={tab === 'auction' ? 'active' : ''} href="#/inventory/auction">Auction House{me?.houseAuction?.status === 'open' && <span className="badge">Live</span>}</a>
        <a className={tab === 'trades' ? 'active' : ''} href="#/inventory/trades">Trades{me?.pendingTrades > 0 && <span className="badge">{me.pendingTrades}</span>}</a>
        <a className={tab === 'collections' ? 'active' : ''} href="#/inventory/collections">Collections</a>
      </div>
      {tab === 'auction' ? <AuctionTab /> : tab === 'trades' ? <TradesTab /> : !data || !me ? <Loading /> : tab === 'store' ? <Store owned={data.items} reload={reload} /> : tab === 'market' ? <MarketTab owned={data.items} reloadInventory={reload} /> : tab === 'collections' ? <Collections data={data} act={act} reload={reload} /> : <Owned data={data} reload={reload} />}
    </div>
  );
}

function Owned({ data, reload }: { data: any; reload: () => void }) {
  const { byId, me, act, content } = useGame();
  const owned = new Map<string, number>(data.items.map((i: any) => [i.itemId, i.qty]));
  const sell = async (itemId: string) => {
    const item = byId.items.get(itemId);
    const value = item.price !== null ? Math.floor((item.price * content.rules.itemSellBackPct) / 100) : item.sellValue;
    if (!confirm(`Sell one ${item.name} for ${money(value)}?`)) return;
    await act('/api/store/sell', { itemId, qty: 1 }, { success: (r) => ({ kind: 'good', text: <>Sold for <span className="money">{money(r.value)}</span>.</> }) });
    reload();
  };
  return (
    <div>
      <Panel title="Mafia Loadout">
        <div className="row" style={{ gap: 24 }}>
          <div><StatIcon kind="mafia" /> Fighters: <b>{data.fighters}</b> <span className="small muted">(mafia of {me.mafiaSize})</span></div>
          <div><StatIcon kind="attack" /> Attack strength: <b>{fmt(data.attackStrength)}</b> <span className="small muted">(gear {fmt(data.equipAttack)})</span></div>
          <div><StatIcon kind="defense" /> Defense strength: <b>{fmt(data.defenseStrength)}</b> <span className="small muted">(gear {fmt(data.equipDefense)})</span></div>
        </div>
        <div className="small muted" style={{ marginTop: 4 }}>
          Each fighter automatically carries one of your best weapons, armor and vehicles. Owning more items than fighters doesn’t help, so grow your mafia too.
        </div>
      </Panel>
      {OWNED_CATS.map(([cat, label]) => {
        const items = [...owned.entries()].map(([id, qty]) => ({ def: byId.items.get(id), qty })).filter((x) => x.def && x.def.category === cat)
          .sort((a, b) => b.def.attack + b.def.defense - (a.def.attack + a.def.defense));
        if (cat === 'consumable' && items.length === 0) return null;
        return (
          <Panel key={cat} title={`${label} (${fmt(items.reduce((s, x) => s + x.qty, 0))})`} right={cat === 'consumable' ? <span className="small dim">gifts from your mafia</span> : <a className="small" onClick={() => go('inventory/store')}>Buy more</a>}>
            {items.length === 0 ? <div className="muted">None yet.</div> : (
              <div className="cards">
                {items.map(({ def, qty }) => (
                  <div className="card" key={def.id} title={def.description}>
                    <ItemIcon itemId={def.id} size={44} />
                    <div className="card-body">
                      <div className="name"><ItemName itemId={def.id} /> <RarityTag rarity={def.rarity} /></div>
                      {cat !== 'supply' && <div className="small"><StatIcon kind="attack" size={10} /> {def.attack} <StatIcon kind="defense" size={10} /> {def.defense}</div>}
                      {cat === 'consumable' ? (
                        <div className="small muted">Owned: {fmt(qty)}{me.isHuman && <> · <a onClick={() => sellOnMarket(def.id)}>trade</a></>} · <a onClick={() => act('/api/items/use', { itemId: def.id }, { success: () => ({ kind: 'good', text: def.effect === 'refill_energy' ? `You ate a ${def.name}. Your energy is full!` : `You used a ${def.name}.` }) }).then(reload)}>use</a></div>
                      ) : def.source === 'gift' ? (
                        <div className="small muted">Owned: {fmt(qty)} · gift{me.isHuman && <> · <a onClick={() => sellOnMarket(def.id)}>trade</a></>}</div>
                      ) : (
                        <div className="small muted">Owned: {fmt(qty)} · <a onClick={() => sell(def.id)}>sell</a>{me.isHuman && <> · <a onClick={() => sellOnMarket(def.id)} title="Sell to another player on the Marketplace">trade</a></>}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        );
      })}
    </div>
  );
}

function Store({ owned, reload }: { owned: any[]; reload: () => void }) {
  const { content, me, act, byId } = useGame();
  const [cat, setCat] = useState('weapon');
  const [qty, setQty] = useState<Record<string, number>>({});
  const have = new Map<string, number>(owned.map((i: any) => [i.itemId, i.qty]));
  const items = content.items.filter((i: any) => i.category === cat && i.price !== null).sort((a: any, b: any) => a.reqLevel - b.reqLevel);
  const buy = async (item: any) => {
    const n = qty[item.id] ?? 1;
    await act('/api/store/buy', { itemId: item.id, qty: n }, { success: (r) => ({ kind: 'good', text: <>You bought {n} × {byId.items.get(item.id).name} for <span className="money">{money(r.cost)}</span>.</> }) });
    reload();
  };
  return (
    <div>
      <div className="subnav" style={{ marginBottom: 8, background: '#000' }}>
        {CATS.map(([c, label]) => <a key={c} className={cat === c ? 'active' : ''} onClick={() => setCat(c)}>{label}</a>)}
      </div>
      <table className="list stack">
        <thead><tr><th></th><th>Item</th><th>Attack</th><th>Defense</th><th>Owned</th><th>Price</th><th></th></tr></thead>
        <tbody>
          {items.map((item: any) => {
            const locked = me.level < item.reqLevel;
            const n = qty[item.id] ?? 1;
            return (
              <tr key={item.id} className={locked ? 'locked' : ''}>
                <td style={{ width: 50 }}><ItemIcon itemId={item.id} size={42} /></td>
                <td>
                  <div className="job-name">{item.name}</div>
                  <div className="small muted">{item.description}</div>
                  {locked && <div className="small"><span className="locked-stamp">Locked</span> until level {item.reqLevel}</div>}
                </td>
                <td>{cat === 'supply' ? '–' : item.attack}</td>
                <td>{cat === 'supply' ? '–' : item.defense}</td>
                <td>{fmt(have.get(item.id) ?? 0)}</td>
                <td className="money">{money(item.price)}</td>
                <td className="right" style={{ whiteSpace: 'nowrap' }}>
                  <select value={n} onChange={(e) => setQty({ ...qty, [item.id]: Number(e.target.value) })} disabled={locked}>
                    {[1, 5, 10, 25, 50, me.fighters].filter((x, i, a) => x > 0 && a.indexOf(x) === i).sort((a, b) => a - b).map((x) => <option key={x} value={x}>{x}{x === me.fighters ? ' (1 per fighter)' : ''}</option>)}
                  </select>{' '}
                  <button className={`btn ${locked ? 'disabled' : ''}`} onClick={() => buy(item)}>Buy</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Collections({ data, act, reload }: { data: any; act: any; reload: () => void }) {
  const { byId } = useGame();
  const stat: Record<string, string> = { attack: 'Attack', defense: 'Defense', max_health: 'Max Health', max_energy: 'Max Energy', max_stamina: 'Max Stamina' };
  return (
    <div>
      <p className="muted">Collection pieces turn up at random while doing jobs in the matching tier. Vault a full set for a reward item, plus a permanent bonus the first time.</p>
      {data.collections.map((c: any) => {
        const def = byId.collections.get(c.collectionId);
        const tier = byId.tiers.get(def.tierId);
        return (
          <Panel key={c.collectionId} title={<>{def.name} <span className="muted small">({tier.name} jobs)</span>{c.timesCompleted > 0 && <span className="gold small"> · vaulted {c.timesCompleted}×</span>}</>}
            right={<span className="small">Bonus: +{def.bonusAmount} {stat[def.bonusStat]} · Reward: <ItemName itemId={def.rewardItem} /></span>}>
            <div className="row" style={{ flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {c.pieces.map((p: any) => (
                <div key={p.itemId} className="center" style={{ width: 70, opacity: p.qty ? 1 : 0.3 }} title={byId.items.get(p.itemId).name}>
                  <ItemIcon itemId={p.itemId} size={40} />
                  <div className="small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{byId.items.get(p.itemId).name}</div>
                  <div className="small muted">×{p.qty}</div>
                </div>
              ))}
              <div className="grow right">
                <button className={`btn ${c.complete ? 'green' : 'disabled'}`} onClick={async () => {
                  await act('/api/collections/vault', { collectionId: c.collectionId }, { success: (r: any) => ({ kind: 'good', text: <>Collection vaulted! You received <ItemName itemId={r.rewardItem} />{r.first ? ' and a permanent bonus' : ''}.</> }) });
                  reload();
                }}>Vault</button>
              </div>
            </div>
          </Panel>
        );
      })}
    </div>
  );
}
